'use client';

import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Routes } from '@/config/routes';
import Input from '@/components/ui/form-fields/input';
import Button from '@/components/ui/button';
import Checkbox from '@/components/ui/form-fields/checkbox';
import Select from '@/components/ui/form-fields/select';

const signUpSchema = z
  .object({
    firstName: z.string(),
    lastName: z.string(),
    email: z
      .string()
      .min(1, 'The email is required.')
      .email({ message: 'The email is invalid.' }),
    password: z
      .string()
      .min(8, { message: 'Password must be 8 characters long.' }),
    confirmPassword: z
      .string()
      .min(8, { message: 'Password must be 8 characters long.' }),
    country: z.string().min(1, 'Country is required.'),
    acceptPolicy: z.boolean(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match.",
    path: ['confirmPassword'],
  });

const countries = [
  { value: 'us', label: 'United States' },
  { value: 'ca', label: 'Canada' },
  { value: 'uk', label: 'United Kingdom' },
  { value: 'au', label: 'Australia' },
  { value: 'pk', label: 'Pakistan' },
  { value: 'in', label: 'India' },
  { value: 'de', label: 'Germany' },
];

type SignUpType = z.infer<typeof signUpSchema>;

export default function SignUpForm() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpType>({
    resolver: zodResolver(signUpSchema),
  });

  const router = useRouter();
  const burl = process.env.NEXT_PUBLIC_API;

  async function handleFormSubmit(data: SignUpType) {
    try {
      const response = await fetch(`${burl}/register.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const userData = {
          name: 'Admin',
          id: result.userID,
          avatar:
            'https://s3.amazonaws.com/redqteam.com/isomorphic-reloaded-image/profilepic.png',
          role: 'admin',
        };

        router.push(Routes.public.dashboard);
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('userId', userData.id);
        }
        localStorage.setItem('loggedUser', JSON.stringify(userData));
        localStorage.setItem('isAuthorized', JSON.stringify(true));
      } else {
        console.error('Error:', result);
      }
    } catch (error) {
      console.error('Network error:', error);
    }
  }

  return (
    <form noValidate onSubmit={handleSubmit((d) => handleFormSubmit(d))}>
      <div className="flex items-center justify-between gap-3">
        <Input
          type="text"
          label="First name"
          className="mb-4"
          error={errors?.firstName?.message}
          required
          {...register('firstName')}
        />
        <Input
          type="text"
          label="Last name"
          className="mb-4"
          error={errors?.lastName?.message}
          {...register('lastName')}
        />
      </div>
      <Input
        type="text"
        label="Email"
        className="mb-4"
        error={errors?.email?.message}
        required
        {...register('email')}
      />
      <div className="flex items-center justify-between gap-3">
        <Input
          type="password"
          label="Password"
          className="mb-4"
          error={errors?.password?.message}
          required
          {...register('password')}
        />
        <Input
          type="password"
          label="Confirm password"
          className="mb-4"
          error={errors?.confirmPassword?.message}
          required
          {...register('confirmPassword')}
        />
      </div>
      <Select
        label="Country"
        className="mb-4"
        error={errors?.country?.message}
        options={countries}
        {...register('country')}
      />
      <Checkbox
        label={
          <>
            <span className="font-normal">I’ve read and agree with </span>
            <Link href="/" className="underline">
              Terms of Service and our Privacy Policy.
            </Link>
          </>
        }
        size="sm"
        className="mb-7"
        labelClassName="ml-3"
        containerClassName="!items-start"
        inputClassName="!text-gray-dark"
        {...register('acceptPolicy')}
      />
      <Button type="submit" className="mb-2 w-full" size="xl">
        Sign Up
      </Button>
      <p className="text-sm leading-6 text-gray">
        Already have an account? &nbsp;
        <Link
          href={Routes.auth.signIn}
          className="font-semibold text-primary underline"
        >
          Sign In
        </Link>
      </p>
    </form>
  );
}
